import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, Typography, Container, Paper } from '@mui/material';
import { useAuth } from '../hooks/useAuth';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: ('athlete' | 'trainer' | 'both' | 'superuser')[];
  requireAuth?: boolean;
}

/**
 * ProtectedRoute component for authentication and role-based access control.
 * 
 * @param children - The child components to render if authorized
 * @param requiredRoles - Array of roles that are allowed to access this route
 * @param requireAuth - Whether authentication is required (default: true)
 */
export default function ProtectedRoute({
  children,
  requiredRoles = [],
  requireAuth = true,
}: ProtectedRouteProps) {
  const { isAuthenticated, user, role } = useAuth();
  const location = useLocation();

  // If authentication is not required, render children directly
  if (!requireAuth) {
    return <>{children}</>;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access if requiredRoles is specified
  if (requiredRoles.length > 0 && role) {
    const hasRequiredRole = requiredRoles.includes(role as 'athlete' | 'trainer' | 'superuser');
    
    // Superuser has access to all routes
    const isSuperuser = role === 'superuser';
    
    if (!hasRequiredRole && !isSuperuser) {
      return (
        <Container maxWidth="md" sx={{ mt: 8 }}>
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h4" component="h1" gutterBottom color="error">
              Access Denied
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              You do not have permission to access this page.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Required role(s): {requiredRoles.join(', ')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Your role: {role}
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2">
                <a href="/" style={{ color: '#0d47a1', textDecoration: 'none' }}>
                  Return to Home
                </a>
              </Typography>
            </Box>
          </Paper>
        </Container>
      );
    }
  }

  // User is authenticated and has required role (or no role check needed)
  return <>{children}</>;
}